import type { IconName } from "@zapier/zinnia-icons";
import type { SearchResultItem } from "../schema";
import type {
  ResourceType,
  SearchEntry,
  SearchSection,
  UniversalSearchResult,
} from "./types";

export const ASSET_TYPES: ResourceType[] = [
  "Agent",
  "Canvas",
  "Chatbot",
  "Folder",
  "Interface",
  "Table",
  "Zap",
];

export const RESOURCE_TYPES: Record<string, ResourceType> = {
  guideposts: "Guidepost",
  apps: "App",
  templates: "Template",
  blogs: "Blog",
  communityQuestions: "CommunityQuestion",
  helpArticles: "HelpArticle",
};

export const GQL_TYPE_TO_ASSET_TYPE: Record<string, ResourceType> = {
  UniversalSearchAgent: "Agent",
  UniversalSearchCanvas: "Canvas",
  UniversalSearchChatbot: "Chatbot",
  UniversalSearchFolder: "Folder",
  UniversalSearchInterface: "Interface",
  UniversalSearchTable: "Table",
  UniversalSearchZap: "Zap",
};

export function getAssetIcon(result: SearchEntry): IconName {
  switch (result.__typename) {
    case "UniversalSearchAgent": {
      return "navAgents";
    }
    case "UniversalSearchCanvas": {
      return "navCanvas";
    }
    case "UniversalSearchChatbot": {
      return "navAIChatbot";
    }
    case "UniversalSearchFolder": {
      return "navFolderFill";
    }
    case "UniversalSearchInterface": {
      return "navInterfaces";
    }
    case "UniversalSearchTable": {
      return "navTables";
    }
    case "UniversalSearchZap": {
      return "miscBoltAltFill";
    }
    default: {
      return "miscBoltAltFill";
    }
  }
}

export const ASSET_TYPE_ICON_COLOR_MAP: Record<string, string> = {
  agent: "UiBrand",
  canvas: "UiBrand",
  chatbot: "UiBrand",
  folder: "GrayWarm6",
  interface: "UiBrand",
  nextGenZap: "UiBrand",
  table: "UiBrand",
  zap: "UiBrand",
};

export function getAssetIconColor(assetType: string): string {
  return ASSET_TYPE_ICON_COLOR_MAP[assetType] ?? "UiBrand";
}

export const ASSET_TYPE_ICON_MAP: Record<string, IconName> = {
  agent: "navAgents",
  canvas: "navCanvas",
  chatbot: "navAIChatbot",
  folder: "navFolder",
  interface: "navInterfaces",
  nextGenZap: "miscTriggerFill",
  table: "navTables",
  zap: "miscBoltAltFill",
};

function getAssetDomain(): string {
  if (globalThis.location === undefined) {
    const origin = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN ?? "";
    return origin.includes("staging") ? "zapier-staging.com" : "zapier.com";
  }
  const { hostname } = globalThis.location;
  if (hostname.includes("staging") || hostname.includes("localhost")) {
    return "zapier-staging.com";
  }
  return "zapier.com";
}

export const ASSET_TYPE_URL_MAP: Record<string, (id: string) => string> = {
  zap: (id) => `/webintent/edit-zap/${id}`,
  interface: (id) =>
    `https://interfaces.${getAssetDomain()}/interfaces/project/${id}`,
  table: (id) => `https://tables.${getAssetDomain()}/app/tables/t/${id}`,
  chatbot: (id) => `/app/chatbots/${id}`,
  canvas: (id) => `/app/canvas/${id}`,
  agent: (id) => `https://agents.${getAssetDomain()}/bots/${id}/settings`,
  folder: (id) => `/app/assets/zaps/folders/${id}`,
};

/** Prefers a server-provided URL over the client-derived map; falls back to `"#"` if neither has one. */
export function getAssetUrl(
  assetType: string,
  id: string,
  url?: string,
): string {
  return url ?? ASSET_TYPE_URL_MAP[assetType]?.(id) ?? "#";
}

/** Narrows a search result to the server-provided URL it carries, if any. */
export function getResultUrl(result: SearchResultItem): string | undefined {
  return result.assetType === "nextGenZap" ? result.editorUrl : undefined;
}

export function getAssetIconByType(assetType: string): IconName {
  return ASSET_TYPE_ICON_MAP[assetType] ?? "miscBoltAltFill";
}

export function getSearchSections(
  searchResults: UniversalSearchResult,
): SearchSection[] {
  return [
    {
      key: "apps",
      algoliaIndex: "apps",
      icon: undefined,
      iconColor: undefined,
      data: searchResults.apps,
    },
    {
      key: "templates",
      algoliaIndex: "templates",
      icon: "navTemplate",
      iconColor: "GrayWarm6",
      data: searchResults.templates,
    },
    {
      key: "blogs",
      algoliaIndex: "blog",
      icon: "fileGuide",
      iconColor: "UiPrimary",
      data: searchResults.blogs,
    },
    {
      key: "communityQuestions",
      algoliaIndex: "community",
      icon: "personGroup",
      iconColor: "UiPrimary",
      data: searchResults.communityQuestions,
    },
    {
      key: "helpArticles",
      algoliaIndex: "help",
      icon: "navHelp",
      iconColor: "UiPrimary",
      data: searchResults.helpArticles,
    },
  ];
}

/**
 * Prompt suggestion categories -- predefined prompts with translation keys.
 */
export const PROMPT_CATEGORIES = {
  "lead-management": {
    categoryKey: "categories.lead-management",
    icon: "arrowBranch" as IconName,
    promptKeys: [
      "prompts.lead-management.assign-leads",
      "prompts.lead-management.score-enrich",
      "prompts.lead-management.advance-stage",
      "prompts.lead-management.merge-duplicates",
    ],
  },
  "customer-support": {
    categoryKey: "categories.customer-support",
    icon: "commentConvo" as IconName,
    promptKeys: [
      "prompts.customer-support.triage-tickets",
      "prompts.customer-support.convert-bugs",
      "prompts.customer-support.daily-summary",
      "prompts.customer-support.vip-alerts",
    ],
  },
  "marketing-growth": {
    categoryKey: "categories.marketing-growth",
    icon: "partnerDashboard" as IconName,
    promptKeys: [
      "prompts.marketing-growth.multi-channel",
      "prompts.marketing-growth.segment-contacts",
      "prompts.marketing-growth.utm-links",
      "prompts.marketing-growth.email-sequences",
    ],
  },
  "project-management": {
    categoryKey: "categories.project-management",
    icon: "navBriefcase" as IconName,
    promptKeys: [
      "prompts.project-management.action-items",
      "prompts.project-management.standup-summary",
      "prompts.project-management.project-brief",
      "prompts.project-management.status-report",
    ],
  },
} as const;

export type CategoryKey = keyof typeof PROMPT_CATEGORIES;

interface ForYouCategory {
  categoryKey: "categories.for-you";
  icon: IconName;
  prompts: string[];
}

interface PredefinedCategory {
  categoryKey: string;
  icon: IconName;
  promptKeys: readonly string[];
}

export function getPromptSuggestionsConfig(
  appAgnosticUseCases: string[],
): Record<string, ForYouCategory | PredefinedCategory> {
  if (appAgnosticUseCases.length > 0) {
    return {
      "for-you": {
        categoryKey: "categories.for-you",
        icon: "personCircle",
        prompts: appAgnosticUseCases,
      },
      "lead-management": PROMPT_CATEGORIES["lead-management"],
      "customer-support": PROMPT_CATEGORIES["customer-support"],
      "marketing-growth": PROMPT_CATEGORIES["marketing-growth"],
    };
  }

  return PROMPT_CATEGORIES;
}

/**
 * Default English labels for prompt categories and prompts.
 * Consumers can override with their own i18n solution.
 */
export const DEFAULT_PROMPT_LABELS: Record<string, string> = {
  "categories.for-you": "For you",
  "categories.lead-management": "Lead management",
  "categories.customer-support": "Customer support",
  "categories.marketing-growth": "Marketing & growth",
  "categories.project-management": "Project management",
  "prompts.lead-management.assign-leads":
    "Automatically assign new leads to the right sales rep",
  "prompts.lead-management.score-enrich":
    "Score and enrich leads from form submissions",
  "prompts.lead-management.advance-stage":
    "Advance deal stage when a contract is signed",
  "prompts.lead-management.merge-duplicates":
    "Detect and merge duplicate contacts",
  "prompts.customer-support.triage-tickets":
    "Triage support tickets by urgency and topic",
  "prompts.customer-support.convert-bugs":
    "Convert bug reports into engineering tasks",
  "prompts.customer-support.daily-summary":
    "Send a daily summary of open tickets to Slack",
  "prompts.customer-support.vip-alerts":
    "Alert the team when a VIP opens a ticket",
  "prompts.marketing-growth.multi-channel":
    "Launch a multi-channel campaign from a single brief",
  "prompts.marketing-growth.segment-contacts":
    "Segment contacts based on engagement score",
  "prompts.marketing-growth.utm-links":
    "Auto-generate UTM links for every campaign",
  "prompts.marketing-growth.email-sequences":
    "Trigger email sequences from webinar sign-ups",
  "prompts.project-management.action-items":
    "Extract action items from meeting notes",
  "prompts.project-management.standup-summary":
    "Post a daily standup summary to your channel",
  "prompts.project-management.project-brief":
    "Generate a project brief from a requirements doc",
  "prompts.project-management.status-report":
    "Compile a weekly status report from task updates",
  "headings.category-prompts": 'Try "{category}"',
};
